import React, { useState, useEffect } from 'react';

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full">
        {children}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
        >
          ×
        </button>
      </div>
    </div>
  );
};


const KanbanBoard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  interface Board {
    ID: string;
    name: string;
    Title: string;
    columns: {
      id: string;
      title: string;
      tasks: Task[];
      order: number;
    }[];
  }
  const [boards, setBoards] = useState<Board[]>([]);
  const [activeBoard, setActiveBoard] = useState<string | null>(null);
  const [newBoardName, setNewBoardName] = useState('');
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const [newTaskTexts, setNewTaskTexts] = useState({}); // Separate state for each column's input
  const [draggedTask, setDraggedTask] = useState<{ boardId: string; columnId: string; taskId: string } | null>(null);
  const [draggedColumn, setDraggedColumn] = useState<{ boardId: string; columnId: string } | null>(null);
  const [taskCategories] = useState(['Bug', 'Feature', 'Enhancement', 'Documentation']);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  interface Task {
    id: string;
    text: string;
    category: string;
    dueDate: string | null;
    description: string;
    createdAt: string;
    labels: string[];
  }

  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  useEffect(() => {
    const fetchBoards = async () => {
      try {
        setLoading(true);
        const response = await fetch("/api/kanban", {
          method: "GET",
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        // Transform API data to match expected format
        const formattedBoards = data.map(board => ({
          ID: board.ID,
          name: board.Title || "Untitled Board",
          Title: board.Title || "Untitled Board",
          columns: board.Columns ? JSON.parse(atob(board.Columns)) : []
        }));

        // If no boards or empty board, use fallback
        if (formattedBoards.length === 0 || !formattedBoards[0].columns) {
          const fallbackBoard = {
            ID: '1',
            title: 'Main Board',
            name: 'Main Board',
            columns: [
              { id: '1', title: 'To Do', tasks: [], order: 0 },
              { id: '2', title: 'In Progress', tasks: [], order: 1 },
              { id: '3', title: 'Done', tasks: [], order: 2 }
            ]
          };
          setBoards([{ ...fallbackBoard, Title: fallbackBoard.name }]);
          setActiveBoard("1");
        } else {
          setBoards(formattedBoards);
          if (!activeBoard) {
            setActiveBoard(formattedBoards[0].ID);
          }
        }
      } catch (error) {
        console.error("Error fetching Kanban data:", error);
        // Load fallback data if database fetch fails
        const fallbackBoard = {
          ID: '1',
          name: 'Main Board',
          columns: [
            { id: '1', title: 'To Do', tasks: [], order: 0 },
            { id: '2', title: 'In Progress', tasks: [], order: 1 },
            { id: '3', title: 'Done', tasks: [], order: 2 }
          ]
        };
        setBoards([{ ...fallbackBoard, Title: fallbackBoard.name }]);
        setActiveBoard("1");
      } finally {
        setLoading(false);
      }
    };

    fetchBoards();
  }, [activeBoard]);

  const saveBoard = async (updatedBoards) => {
    console.log("Saving board:", updatedBoards);
    try {
      const response = await fetch("/api/kanban", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedBoards),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setBoards(updatedBoards);
    } catch (error) {
      console.error("Error saving board:", error);
      setError("Failed to save changes. Please try again.");
    }
  };

  const getCurrentBoard = () => boards.find(b => b.ID === activeBoard);



  const KanbanColumn = ({ column, updatedBoards }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [title, setTitle] = useState(column.title);
    console.log("updatedBoards", updatedBoards);
    console.log("column", column);
    console.log("column title", column.title);
    const handleTitleClick = () => {
      setIsEditing(true);
    };

    const handleTitleChange = (e) => {
      setTitle(e.target.value);
    };

    const handleTitleBlur = (column, title, boards) => {
      setIsEditing(false);
      column.title = title;
      saveBoard(boards);
    };

    return (
      <div>
        {isEditing ? (
          <input
            type="text"
            value={title}
            onChange={handleTitleChange}
            onBlur={() => handleTitleBlur(column, title, updatedBoards)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleTitleBlur(column, title, boards);
              }
            }}
            className="font-bold text-lg"
            autoFocus
          />
        ) : (
          <h3 className="font-bold text-lg" onClick={handleTitleClick}>
            {title}
          </h3>
        )}
      </div>
    );
  };

  const addNewBoard = () => {
    if (!newBoardName.trim()) return;

    const newBoard: Board = {
      ID: Date.now().toString(),
      name: newBoardName,
      Title: newBoardName,
      columns: [
        { id: '1', title: 'To Do', tasks: [], order: 0 },
        { id: '2', title: 'In Progress', tasks: [], order: 1 },
        { id: '3', title: 'Done', tasks: [], order: 2 }
      ]
    };
    const updatedBoards = [...boards, newBoard];
    setBoards(updatedBoards);
    saveBoard(updatedBoards);
    setNewBoardName('');
  };

  const addNewColumn = () => {
    if (!newColumnTitle.trim()) return;

    const updatedBoards = boards.map(board => {
      if (board.ID === activeBoard) {
        return {
          ...board,
          columns: [
            ...board.columns,
            {
              id: Date.now().toString(),
              title: newColumnTitle,
              tasks: [],
              order: board.columns.length
            }
          ]
        };
      }
      return board;
    });
    setBoards(updatedBoards);
    saveBoard(updatedBoards);
    setNewColumnTitle('');
  };

  const addNewTask = (columnId) => {
    const taskText = newTaskTexts[columnId];
    if (!taskText?.trim()) return;

    const newTask = {
      id: Date.now().toString(),
      text: taskText,
      category: taskCategories[0],
      dueDate: null,
      description: '',
      createdAt: new Date().toISOString(),
      labels: []
    };

    const updatedBoards = boards.map(board => {
      if (board.ID === activeBoard) {
        return {
          ...board,
          columns: board.columns.map(col => {
            if (col.id === columnId) {
              return {
                ...col,
                tasks: [...col.tasks, newTask]
              };
            }
            return col;
          })
        };
      }
      return board;
    });
    setBoards(updatedBoards);
    saveBoard(updatedBoards);

    // Clear only the specific column's input
    setNewTaskTexts(prev => ({
      ...prev,
      [columnId]: ''
    }));
  };

  const handleDragStart = (e, boardId, columnId, taskId) => {
    e.stopPropagation();
    setDraggedTask({ boardId, columnId, taskId });
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '0.4';
    }
  };

  const handleDragEnd = (e) => {
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '1';
    }
    saveBoard(boards);
    setDraggedTask(null);
    setDraggedColumn(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetBoardId, targetColumnId) => {
    e.preventDefault();
    e.stopPropagation();

    // Handle task drop
    if (draggedTask) {
      const { boardId: sourceBoardId, columnId: sourceColumnId, taskId } = draggedTask;

      const updatedBoards = boards.map(board => {
        if (board.ID === sourceBoardId) {
          // Find the task to move
          const sourceColumn = board.columns.find(col => col.id === sourceColumnId);
          const taskToMove = sourceColumn?.tasks.find(task => task.id === taskId);

          if (!taskToMove) return board;

          return {
            ...board,
            columns: board.columns.map(col => {
              if (col.id === sourceColumnId) {
                // Remove task from source column
                return {
                  ...col,
                  tasks: col.tasks.filter(task => task.id !== taskId)
                };
              }
              if (board.ID === targetBoardId && col.id === targetColumnId) {
                // Add task to target column
                return {
                  ...col,
                  tasks: [...col.tasks, taskToMove]
                };
              }
              return col;
            })
          };
        }
        return board;
      });
      setBoards(updatedBoards);
      saveBoard(updatedBoards);
      setDraggedTask(null);
    }
  };

  const handleColumnDragStart = (e, columnId) => {
    if (draggedTask) return; // Prevent column drag if we're dragging a task
    setDraggedColumn({ boardId: activeBoard || "", columnId });
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '0.4';
    }
  };

  const handleColumnDrop = (e, targetColumnId) => {
    e.preventDefault();

    if (!draggedColumn || draggedColumn.columnId === targetColumnId) return;

    const updatedBoards = boards.map(board => {
      if (board.ID === activeBoard) {
        const newColumns = [...board.columns];
        const draggedColumnIndex = newColumns.findIndex(col => col.id === draggedColumn.columnId);
        const targetColumnIndex = newColumns.findIndex(col => col.id === targetColumnId);

        // Move the column
        const [removedColumn] = newColumns.splice(draggedColumnIndex, 1);
        newColumns.splice(targetColumnIndex, 0, removedColumn);

        // Update column orders
        return {
          ...board,
          columns: newColumns.map((col, index) => ({ ...col, order: index }))
        };
      }
      return board;
    });
    setBoards(updatedBoards);
    saveBoard(updatedBoards);
    setDraggedColumn(null);
  };

  const openTaskModal = (task) => {
    setSelectedTask(task);
    setIsTaskModalOpen(true);
  };

  const removeColumn = (columnId) => {
    const updatedBoards = boards.map(board => {
      if (board.ID === activeBoard) {
        return {
          ...board,
          columns: board.columns.filter(col => col.id !== columnId)
        };
      }
      return board;
    });
    setBoards(updatedBoards);
    saveBoard(updatedBoards);
  };

  const removeTask = (columnId, taskId) => {
    const updatedBoards = boards.map(board => {
      if (board.ID === activeBoard) {
        return {
          ...board,
          columns: board.columns.map(col => {
            if (col.id === columnId) {
              return {
                ...col,
                tasks: col.tasks.filter(task => task.id !== taskId)
              };
            }
            return col;
          })
        };
      }
      return board;
    });
    setBoards(updatedBoards);
    saveBoard(updatedBoards);
  };

  const updateTask = (updatedTask: Task) => {
    const updatedBoards = boards.map(board => {
      if (board.ID === activeBoard) {
        return {
          ...board,
          columns: board.columns.map(col => {
            return {
              ...col,
              tasks: col.tasks.map(task => task.id === updatedTask.id ? updatedTask : task)
            };
          })
        };
      }
      return board;
    });
    setBoards(updatedBoards);
    saveBoard(updatedBoards);
    setIsTaskModalOpen(false);
  };

  if (loading) {
    return <div className="p-4">Loading boards...</div>;
  }

  if (error) {
    return (
      <div className="p-4">
        {error && (
          <div className="mb-4 p-4 bg-yellow-100 text-yellow-800 rounded">
            Warning: {error}
            <button
              onClick={() => window.location.reload()}
              className="ml-4 px-4 py-2 bg-blue-500 text-white rounded"
            >
              Retry
            </button>
          </div>
        )}
      </div>
    )
  };

  return (
    <div className="p-4">
      {/* Board Selection */}
      <div className="mb-4 flex gap-4">
        <select
          value={activeBoard || ""}
          onChange={(e) => setActiveBoard(e.target.value)}
          className="px-3 py-2 border rounded"
        >
          {boards.map(board => (
            <option key={board.ID} value={board.ID}>{board.Title || "Untitled Board"}</option>
          ))}
        </select>

        <input
          type="text"
          value={newBoardName}
          onChange={(e) => setNewBoardName(e.target.value)}
          placeholder="New Board Name"
          className="px-3 py-2 border rounded"
        />
        <button
          onClick={addNewBoard}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Add Board
        </button>
      </div>

      {/* Column Creation */}
      <div className="mb-4 flex gap-4">
        <input
          type="text"
          value={newColumnTitle}
          onChange={(e) => setNewColumnTitle(e.target.value)}
          placeholder="New Column Title"
          className="px-3 py-2 border rounded"
        />
        <button
          onClick={addNewColumn}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Add Column
        </button>
      </div>

      {/* Columns */}
      <div className="flex gap-4 overflow-x-auto min-h-96">
        {getCurrentBoard()?.columns.sort((a, b) => a.order - b.order).map(column => (
          <div
            key={column.id}
            draggable
            onDragStart={(e) => handleColumnDragStart(e, column.id)}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDrop={(e) => {
              if (draggedColumn) {
                handleColumnDrop(e, column.id);
              } else if (draggedTask) {
                handleDrop(e, activeBoard, column.id);
              }
            }}
            className="flex-shrink-0 w-72 bg-gray-100 rounded-lg p-4"
          >
            <div className="flex justify-between items-center mb-4">
              <KanbanColumn column={column} updatedBoards={boards} />
              <button
                onClick={() => removeColumn(column.id)}
                className="text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </div>

            <div className="mb-4">
              <input
                type="text"
                value={newTaskTexts[column.id] || ''}
                onChange={(e) => setNewTaskTexts(prev => ({
                  ...prev,
                  [column.id]: e.target.value
                }))}
                placeholder="New Task"
                className="w-full px-3 py-2 border rounded mb-2"
              />
              <button
                onClick={() => addNewTask(column.id)}
                className="w-full px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Add Task
              </button>
            </div>

            <div
              className="space-y-2"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, activeBoard, column.id)}
            >
              {column.tasks.map(task => (
                <div
                  key={task.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, activeBoard, column.id, task.id)}
                  onDragEnd={handleDragEnd}
                  onClick={() => openTaskModal(task)}
                  className="bg-white p-3 rounded shadow cursor-move hover:bg-gray-50"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium">{task.text}</div>
                      {task.category && (
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                          {task.category}
                        </span>
                      )}
                      {task.dueDate && (
                        <div className="text-xs text-gray-500 mt-1">
                          Due: {new Date(task.dueDate).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeTask(column.id, task.id);
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Task Modal */}
      <Modal
        isOpen={isTaskModalOpen}
        onClose={() => setIsTaskModalOpen(false)}
      >
        {selectedTask && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">Edit Task</h2>
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <input
                type="text"
                value={selectedTask.text}
                onChange={(e) => setSelectedTask({ ...selectedTask, text: e.target.value })}
                className="w-full px-3 py-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <select
                value={selectedTask.category}
                onChange={(e) => setSelectedTask({ ...selectedTask, category: e.target.value })}
                className="w-full px-3 py-2 border rounded"
              >
                {taskCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Due Date</label>
              <input
                type="date"
                value={selectedTask.dueDate || ''}
                onChange={(e) => setSelectedTask({ ...selectedTask, dueDate: e.target.value })}
                className="w-full px-3 py-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={selectedTask.description}
                onChange={(e) => setSelectedTask({ ...selectedTask, description: e.target.value })}
                className="w-full px-3 py-2 border rounded"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsTaskModalOpen(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => updateTask(selectedTask)}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Save Changes
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default KanbanBoard;